import QRCodeStyling from "qr-code-styling";
import identityHeaderLogo from "../assets/logo2.png";
// Temporary fallback until the requested src/assets/icon.png is supplied.
import qrCenterIconFallback from "../assets/mybuildid_qr_logo_black_red_transparent.png";
import { createOpticallyCenteredQrIconDataUrl, createTintedLogoDataUrl } from "./tintTransparentImage";
import { getMirrorScannableQrColor, getQrSafeColor, getResolvedBaseTheme, getScannableQrColor } from "./identityStudio";
import { identityBaseThemes, identityProductLanguage } from "../components/identity/designSystem/identityDesignTokens";
import { drawIdentityProductLanguage, drawIdentityQrFrame } from "../components/identity/designSystem/identityCanvasPrimitives";
import { MIRROR_TAG_SIZE, mirrorTagLayout } from "../components/identity/designSystem/mirrorTagDesign";
import { getStickerShape } from "../components/identity/designSystem/stickerShapeDesign";

const EXPORT_SIZES = {
  qr: [1600, 1600],
  card: [1950, 1200],
  mirror: MIRROR_TAG_SIZE.pixels,
  sticker: [1800, 2400]
};
const STICKER_QR_VERTICAL_OFFSET_RATIO = .033;

export async function exportIdentityPng({ mode, settings, buildUrl, vehicleTitle = "", vehicleYear = "", vehicleMakeModel = "" }) {
  const [width, height] = EXPORT_SIZES[mode];
  const baseTheme = getResolvedBaseTheme(settings, mode);
  const basePalette = identityBaseThemes[baseTheme];
  const stickerShape = getStickerShape(settings.stickerShape);
  const [tintedHeaderLogo, tintedCenterIcon] = await Promise.all([
    createTintedLogoDataUrl(identityHeaderLogo, settings.accentColor),
    createTintedLogoDataUrl(qrCenterIconFallback, settings.accentColor)
  ]);
  const qrColor = mode === "mirror" ? getMirrorScannableQrColor(settings.accentColor) : ["card", "sticker"].includes(mode) ? getQrSafeColor(settings.accentColor) : getScannableQrColor(settings.accentColor);
  const qrSize = mode === "mirror" ? 1040 : mode === "qr" ? 1600 : 1150;
  const positionedCenterIcon = await createOpticallyCenteredQrIconDataUrl(tintedCenterIcon, qrSize, .2);
  const qrBlob = await createQrBlob({ size: qrSize, settings, buildUrl, centerIcon: positionedCenterIcon, qrColor, cornerColor: mode === "mirror" ? qrColor : "#111111", marginRatio: ["card", "sticker"].includes(mode) ? .0375 : .045 });
  if (mode === "qr") return downloadBlob(qrBlob, "mybuildid-qr-only.png");

  const [qrImage, logoImage] = await Promise.all([loadBlobImage(qrBlob), loadImage(tintedHeaderLogo)]);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("The browser could not create the identity export.");
  if (mode === "sticker") drawStickerCutBorder(context, width, height, stickerShape);

  if (mode === "card") drawLandscapeIdentityCard(context, { width, height, settings, qrImage, logoImage, vehicleTitle, baseTheme, basePalette });
  else {
    context.save();
    if (mode === "mirror") { context.beginPath(); context.rect(0, 0, width, height); }
    else if (mode === "sticker") createInsetStickerPath(context, width, height, Math.max(12, width * .024), stickerShape);
    else createProductPath(context, mode, width, height);
    context.clip();
    context.fillStyle = basePalette.background;
    context.fillRect(0, 0, width, height);
    drawFinish(context, settings.finish, width, height, baseTheme);
    drawIdentityProductLanguage(context, { product: mode, accent: settings.accentColor, width, height, finish: settings.finish, roundedRect, baseTheme, includeEdge: mode !== "mirror" });
    if (mode === "sticker") drawStickerAccentOutline(context, width, height, stickerShape, settings.accentColor);

    const hasMirrorVehicle = mode === "mirror" && settings.showCarName && Boolean(vehicleYear || vehicleMakeModel);
    const layout = getLayout(mode, width, height, hasMirrorVehicle);
    if (mode !== "sticker") drawContainedLogo(context, logoImage, layout.logo);
    if (hasMirrorVehicle) drawMirrorVehicleBlock(context, { year: vehicleYear, makeModel: vehicleMakeModel, width, layout, color: basePalette.textPrimary });
    else if (mode !== "mirror" && mode !== "sticker" && settings.showCarName && vehicleTitle) drawVehicleName(context, vehicleTitle, width / 2, layout.vehicleNameY, width * .72, width * .026, "center", basePalette.textPrimary);
    context.textAlign = "center";
    drawIdentityQrFrame(context, { accent: settings.accentColor, x: layout.qrX, y: layout.qrY, size: layout.qrSize, variant: identityProductLanguage[mode].qrFrame });
    const qrRenderY = mode === "sticker"
      ? layout.qrY + layout.qrSize * STICKER_QR_VERTICAL_OFFSET_RATIO
      : layout.qrY;
    context.drawImage(qrImage, layout.qrX, qrRenderY, layout.qrSize, layout.qrSize);
    if (mode === "sticker") {
      drawContainedLogo(context, logoImage, layout.logo);
      if (settings.showCarName && vehicleTitle) drawVehicleName(context, vehicleTitle, width / 2, layout.vehicleNameY, width * .72, width * .026, "center", basePalette.textPrimary);
    }
    if (mode === "mirror") {
      const divider = context.createLinearGradient(width * .25, 0, width * .75, 0); divider.addColorStop(0, "transparent"); divider.addColorStop(.5, settings.accentColor); divider.addColorStop(1, "transparent");
      context.fillStyle = divider; context.fillRect(width * .25, height * mirrorTagLayout.cta.dividerY, width * .5, Math.max(3, width * .003));
    }
    context.fillStyle = basePalette.textPrimary;
    context.font = `900 ${Math.round(width * .045)}px Arial`;
    context.fillText("SCAN TO VIEW MY BUILD", width / 2, layout.scanY);
    context.fillStyle = settings.accentColor;
    context.font = `900 ${Math.round(width * .036)}px Arial`;
    context.fillText("MYBUILDID.COM", width / 2, layout.siteY);
    context.restore();
  }

  const blob = await canvasToBlob(canvas);
  downloadBlob(blob, `mybuildid-${mode === "mirror" ? "mirror-show-tag" : mode}.png`);
}

function drawLandscapeIdentityCard(context, { width, height, settings, qrImage, logoImage, vehicleTitle, baseTheme, basePalette }) {
  const accent = settings.accentColor;
  context.save();
  context.fillStyle = basePalette.background;
  context.fillRect(0, 0, width, height);
  drawFinish(context, settings.finish, width, height, baseTheme);

  drawIdentityProductLanguage(context, { product: "card", accent, width, height, finish: settings.finish, roundedRect, baseTheme });
  const qrSize = height * .65; const qrX = width * .315; const qrY = (height - qrSize) / 2;
  drawIdentityQrFrame(context, { accent, x: qrX, y: qrY, size: qrSize, variant: identityProductLanguage.card.qrFrame });
  context.drawImage(qrImage, qrX, qrY, qrSize, qrSize);

  drawContainedLogo(context, logoImage, [width * .045, height * .31, width * .21, height * .2]);
  if (settings.showCarName && vehicleTitle) drawVehicleName(context, vehicleTitle, width * .05, height * .59, width * .21, width * .017, "left", basePalette.textPrimary);
  for (let index = 0; index < 5; index += 1) { context.globalAlpha = index % 2 ? .35 : 1; context.fillStyle = accent; context.fillRect(width * (.05 + index * .034), height * .66, width * .027, 7); }
  context.globalAlpha = 1;

  const textX = width * .84;
  context.textAlign = "center"; context.fillStyle = accent; context.fillRect(width * .76, height * .31, width * .16, 7);
  context.fillStyle = basePalette.textPrimary; context.font = `900 ${Math.round(width * .031)}px Arial`; context.fillText("SCAN TO VIEW", textX, height * .43); context.fillText("MY BUILD", textX, height * .5);
  context.fillStyle = accent; context.font = `900 ${Math.round(width * .019)}px Arial`; context.fillText("MYBUILDID.COM", textX, height * .61);
  context.fillStyle = "#777777"; context.font = `800 ${Math.round(width * .009)}px Arial`; context.fillText("VEHICLE PROFILE ACCESS", textX, height * .73);

  context.restore();
}

async function createQrBlob({ size, settings, buildUrl, centerIcon, qrColor, cornerColor, marginRatio }) {
  const color = qrColor;
  const qr = new QRCodeStyling({
    width: size, height: size, type: "canvas", data: buildUrl, margin: Math.round(size * marginRatio),
    qrOptions: { errorCorrectionLevel: "H" },
    backgroundOptions: { color: "#ffffff" },
    dotsOptions: { color, type: settings.qrDotStyle },
    cornersSquareOptions: { color: cornerColor, type: settings.qrCornerSquareStyle },
    cornersDotOptions: { color, type: settings.qrCornerDotStyle },
    image: settings.centerLogo === "mbid" ? centerIcon : undefined,
    imageOptions: { crossOrigin: "anonymous", margin: Math.round(size * .012), imageSize: .2, hideBackgroundDots: true }
  });
  const blob = await qr.getRawData("png");
  if (!blob) throw new Error("The QR export could not be generated.");
  return blob;
}

function createProductPath(context, mode, width, height) {
  context.beginPath();
  if (mode === "mirror") roundedRect(context, 0, 0, width, height, width * .1);
  else if (mode === "card") context.rect(0, 0, width, height);
  else {
    const radius = width * .08;
    context.moveTo(radius, 0); context.lineTo(width * .86, 0); context.lineTo(width, width * .14);
    context.lineTo(width, height - radius); context.quadraticCurveTo(width, height, width - radius, height);
    context.lineTo(radius, height); context.quadraticCurveTo(0, height, 0, height - radius);
    context.lineTo(0, radius); context.quadraticCurveTo(0, 0, radius, 0); context.closePath();
  }
}

function getLayout(mode, width, height, hasMirrorVehicle = false) {
  if (mode === "mirror") return { logo: [width * mirrorTagLayout.logo.x, height * (hasMirrorVehicle ? mirrorTagLayout.logo.yWithVehicle : mirrorTagLayout.logo.yWithoutVehicle), width * mirrorTagLayout.logo.width, height * mirrorTagLayout.logo.height], vehicleYearY: height * mirrorTagLayout.vehicle.yearY, vehicleModelY: height * mirrorTagLayout.vehicle.modelY, qrSize: width * mirrorTagLayout.qr.size, qrX: width * mirrorTagLayout.qr.x, qrY: height * (hasMirrorVehicle ? mirrorTagLayout.qr.yWithVehicle : mirrorTagLayout.qr.yWithoutVehicle), scanY: height * mirrorTagLayout.cta.scanY, siteY: height * mirrorTagLayout.cta.siteY };
  if (mode === "sticker") return { logo: [width * .24, height * .075, width * .52, height * .115], vehicleNameY: height * .235, qrSize: width * .6, qrX: width * .2, qrY: height * .27, scanY: height * .79, siteY: height * .84 };
  return { logo: [width * .22, height * .07, width * .56, height * .12], qrSize: width * .62, qrX: width * .19, qrY: height * .27, scanY: height * .8, siteY: height * .85 };
}

function drawFinish(context, finish, width, height, baseTheme = "dark") {
  if (finish === "minimal" || finish === "matte") return;
  context.save(); context.globalAlpha = finish === "gloss" ? (baseTheme === "light" ? .18 : .1) : (baseTheme === "light" ? .055 : .075);
  if (finish === "gloss") {
    const reflection = context.createLinearGradient(0, 0, width, height); reflection.addColorStop(0, baseTheme === "light" ? "#fff" : "#fff"); reflection.addColorStop(.38, "transparent"); reflection.addColorStop(1, baseTheme === "light" ? "#c8c8c8" : "#fff"); context.fillStyle = reflection; context.fillRect(0, 0, width, height);
  } else {
    context.strokeStyle = baseTheme === "light" ? (finish === "brushed-metal" ? "#777" : "#999") : (finish === "brushed-metal" ? "#fff" : "#aaa");
    const spacing = finish === "carbon-fiber" ? 34 : 22;
    for (let offset = -height; offset < width + height; offset += spacing) { context.beginPath(); context.moveTo(offset, 0); context.lineTo(offset - height, height); context.stroke(); }
  }
  context.restore();
}

function drawContainedLogo(context, image, [x, y, width, height]) {
  const scale = Math.min(width / image.naturalWidth, height / image.naturalHeight);
  const drawWidth = image.naturalWidth * scale;
  const drawHeight = image.naturalHeight * scale;
  context.drawImage(image, x + (width - drawWidth) / 2, y + (height - drawHeight) / 2, drawWidth, drawHeight);
}

function drawVehicleName(context, value, x, y, maxWidth, maximumFontSize, align = "center", color = "#e8e8e8") {
  let fontSize = maximumFontSize;
  context.textAlign = align;
  context.fillStyle = color;
  do { context.font = `700 ${Math.round(fontSize)}px Arial`; fontSize -= 1; } while (context.measureText(value.toUpperCase()).width > maxWidth && fontSize > maximumFontSize * .65);
  context.fillText(value.toUpperCase(), x, y, maxWidth);
}

function drawMirrorVehicleBlock(context, { year, makeModel, width, layout, color = "#f2f2f2" }) {
  context.save(); context.textAlign = "center"; context.textBaseline = "alphabetic"; context.fillStyle = color;
  if (year) { context.font = `600 ${Math.round(width * .026)}px Arial`; context.fillText(year.toUpperCase(), width / 2, layout.vehicleYearY); }
  if (makeModel) {
    context.font = `900 ${Math.round(width * .041)}px Arial`;
    context.fillText(ellipsizeCanvasText(context, makeModel.toUpperCase(), width * .78), width / 2, layout.vehicleModelY);
  }
  context.restore();
}

function ellipsizeCanvasText(context, value, maxWidth) {
  if (context.measureText(value).width <= maxWidth) return value;
  let result = value;
  while (result.length > 1 && context.measureText(`${result}…`).width > maxWidth) result = result.slice(0, -1);
  return `${result.trimEnd()}…`;
}

function drawStickerCutBorder(context, width, height, shape) { context.save(); context.strokeStyle = "#f5f5f5"; context.lineWidth = Math.max(12, width * .024); createInsetStickerPath(context, width, height, context.lineWidth / 2, shape); context.stroke(); context.restore(); }
function drawStickerAccentOutline(context, width, height, shape, accent) { context.save(); context.strokeStyle = accent; context.lineWidth = Math.max(4, width * .006); createInsetStickerPath(context, width, height, width * .0315, shape); context.stroke(); context.restore(); }
function createInsetStickerPath(context, width, height, inset, shape = getStickerShape("standard")) {
  const radius = Math.max(0, width * shape.cornerRadiusRatio - inset * .2);
  context.beginPath();
  if (shape.id === "round") { roundedRect(context, inset, inset, width - inset * 2, height - inset * 2, radius); return; }
  context.moveTo(inset + radius, inset); context.lineTo(width * (1 - shape.clipRatio) - inset, inset); context.lineTo(width - inset, inset + width * shape.clipRatio);
  context.lineTo(width - inset, height - inset - radius); context.quadraticCurveTo(width - inset, height - inset, width - inset - radius, height - inset);
  context.lineTo(inset + radius, height - inset); context.quadraticCurveTo(inset, height - inset, inset, height - inset - radius);
  context.lineTo(inset, inset + radius); context.quadraticCurveTo(inset, inset, inset + radius, inset); context.closePath();
}

function roundedRect(context, x, y, width, height, radius) { context.moveTo(x + radius, y); context.arcTo(x + width, y, x + width, y + height, radius); context.arcTo(x + width, y + height, x, y + height, radius); context.arcTo(x, y + height, x, y, radius); context.arcTo(x, y, x + width, y, radius); context.closePath(); }
function loadBlobImage(blob) { const url = URL.createObjectURL(blob); return loadImage(url).finally(() => URL.revokeObjectURL(url)); }
function loadImage(src) { return new Promise((resolve, reject) => { const image = new Image(); image.onload = () => resolve(image); image.onerror = () => reject(new Error("An identity asset could not be loaded.")); image.src = src; }); }
function canvasToBlob(canvas) { return new Promise((resolve, reject) => canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("The PNG could not be encoded.")), "image/png")); }
function downloadBlob(blob, filename) { const url = URL.createObjectURL(blob); const link = document.createElement("a"); link.href = url; link.download = filename; document.body.appendChild(link); link.click(); link.remove(); setTimeout(() => URL.revokeObjectURL(url), 1000); }

export { EXPORT_SIZES };
